import { DocumentNode, gql } from '@apollo/client';
import { graphql } from '@xolv-mybrightlink/common';
import { UserConnection } from '../model/UserConnection';

const FETCH_CONNECTED_CONNECTION_QUERY = gql`
  query connectedConnections($page: Int!, $size: Int!) {
    connectedConnections(paginationData: { page: $page, size: $size }) {
      guid
      userId
      userProfileGuid
      firstName
      lastName
      formattedAddress
      city
      zipCode
      state
      numOfKids
      communityOptIn
    }
  }
`;

const FETCH_INCOMING_CONNECTION_QUERY = gql`
  query incomingConnections($page: Int!, $size: Int!) {
    incomingConnections(paginationData: { page: $page, size: $size }) {
      guid
      userId
      userProfileGuid
      firstName
      lastName
      formattedAddress
      city
      zipCode
      state
      numOfKids
      userAnalyticsId
      communityOptIn
    }
  }
`;

const FETCH_OUTGOING_CONNECTION_QUERY = gql`
  query outgoingConnections($page: Int!, $size: Int!) {
    outgoingConnections(paginationData: { page: $page, size: $size }) {
      guid
      userId
      userProfileGuid
      firstName
      lastName
      formattedAddress
      city
      zipCode
      state
      numOfKids
      communityOptIn
    }
  }
`;

const REQUEST_CONNECTION_QUERY = gql`
  mutation connectPeer($guid: String) {
    connectPeer(guid: $guid)
  }
`;

const CANCEL_CONNECTION_REQUEST_QUERY = gql`
  mutation cancelConnectionRequestWithPeer($peerProfileGuid: String) {
    cancelConnectionRequestWithPeer(peerProfileGuid: $peerProfileGuid)
  }
`;

const REJECT_CONNECTION_REQUEST_QUERY = gql`
  mutation rejectConnectionRequest($userConnectionGuid: String) {
    rejectConnectionRequest(userConnectionGuid: $userConnectionGuid)
  }
`;

const ACCEPT_CONNECTION_REQUEST_QUERY = gql`
  mutation acceptConnectionRequest($userConnectionGuid: String) {
    acceptConnectionRequest(userConnectionGuid: $userConnectionGuid) {
      guid
      userId
      userProfileGuid
      firstName
      lastName
      formattedAddress
      city
      zipCode
      state
      numOfKids
      userAnalyticsId
      communityOptIn
    }
  }
`;

const DISCONNECT_CONNECTION_QUERY = gql`
  mutation disconnectPeer($guid: String) {
    disconnectPeer(guid: $guid)
  }
`;

type ConnectionQueryVariables = {
  page: number;
  size: number;
};
async function fetchUserConnection<R>(
  query: DocumentNode,
  variables: ConnectionQueryVariables,
): Promise<R> {
  const connections = await graphql.query<R, ConnectionQueryVariables>({
    query: query,
    variables: variables,
    fetchPolicy: 'no-cache',
  });
  return connections.data;
}

export const fetchIncomingUserConnection = async (
  page: number,
  size: number,
): Promise<UserConnection[]> => {
  const queryVariables = {
    page: page,
    size: size,
  };
  const incomingConnections = await fetchUserConnection<{
    incomingConnections: UserConnection[];
  }>(FETCH_INCOMING_CONNECTION_QUERY, queryVariables);
  return incomingConnections.incomingConnections;
};

export const fetchOutgoingUserConnection = async (
  page: number,
  size: number,
): Promise<UserConnection[]> => {
  const queryVariables = {
    page: page,
    size: size,
  };
  const outgoingConnections = await fetchUserConnection<{
    outgoingConnections: UserConnection[];
  }>(FETCH_OUTGOING_CONNECTION_QUERY, queryVariables);
  return outgoingConnections.outgoingConnections;
};

export const fetchConnectedUserConnection = async (
  page: number,
  size: number,
): Promise<UserConnection[]> => {
  const queryVariables = {
    page: page,
    size: size,
  };
  const connectedConnections = await fetchUserConnection<{
    connectedConnections: UserConnection[];
  }>(FETCH_CONNECTED_CONNECTION_QUERY, queryVariables);
  return connectedConnections.connectedConnections;
};

export const connectPeer = async (peerGuid: string) => {
  await graphql.mutate({
    mutation: REQUEST_CONNECTION_QUERY,
    variables: {
      guid: peerGuid,
    },
  });
};

export const cancelConnectionRequest = async (peerGuid: string) => {
  await graphql.mutate({
    mutation: CANCEL_CONNECTION_REQUEST_QUERY,
    variables: {
      peerProfileGuid: peerGuid,
    },
  });
};

export const rejectConnectionRequest = async (connectionGuid: string) => {
  await graphql.mutate({
    mutation: REJECT_CONNECTION_REQUEST_QUERY,
    variables: {
      userConnectionGuid: connectionGuid,
    },
  });
};

export const acceptConnectionRequest = async (
  connectionGuid: string,
): Promise<UserConnection | undefined> => {
  const response = await graphql.mutate<
    { acceptConnectionRequest: UserConnection },
    { userConnectionGuid: string }
  >({
    mutation: ACCEPT_CONNECTION_REQUEST_QUERY,
    variables: {
      userConnectionGuid: connectionGuid,
    },
  });
  return response.data?.acceptConnectionRequest;
};

export const disconnectConnection = async (peerGuid: string) => {
  await graphql.mutate({
    mutation: DISCONNECT_CONNECTION_QUERY,
    variables: {
      guid: peerGuid,
    },
  });
};
